/**
 * CustomSidebar — o menu lateral.
 *
 * ORIGEM: o esqueleto (markup e classes) veio do tema AdminUX; **o
 * comportamento é nosso** — quais itens existem, o que cada perfil enxerga
 * (`secao` do RBAC e a marca `gestao`), o modo iconic e o "ver como".
 *
 * Por isso leva `Custom`: a regra do hub é "é nosso? tem Custom", sem exceção.
 * O elo com o catálogo do tema fica aqui, neste aviso, e não no nome do arquivo.
 * O mostruário original está em `theme/adminux-react/src/components/Sidebar.tsx`
 * (raiz do projeto, fora do git).
 */
import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ehCelularAgora } from '@/lib/tela';
import { useAuth, type LojaMenu } from '@/auth/auth-context';

interface SubItem {
  to: string;
  icon: string;
  nome: string;
  /**
   * A permissão do SUB-ITEM. Existe desde 27/08/2026: até então o filho herdava
   * a permissão do grupo, e "Agendamentos" (a agenda do mês, de toda a equipe)
   * abria para qualquer um que alcançasse "Instalações" — o instalador,
   * inclusive. Filho sem permissão declarada segue livre para quem vê o grupo.
   */
  permissao?: string;
}

interface ItemMenu {
  to?: string;
  icon: string;
  nome: string;
  sub?: SubItem[];
  /**
   * A permissão que libera este item — a MESMA string que trava a rota
   * correspondente no backend. Sem permissão declarada = livre para quem logou
   * (Início, e as telas pessoais).
   *
   * Até 09/08/2026 o menu não filtrava NADA: o instalador via Despesas, Custos
   * de Pedido, Autorizações e Pós-venda — telas que a API recusa para ele. Menu
   * que oferece o que dá 403 ensina a pessoa a desconfiar do app.
   *
   * Isto continua sendo conveniência, não segurança: a trava real está nas
   * rotas, porque esconder item não impede ninguém de digitar a URL. A diferença
   * do modelo antigo (`secao` + a marca `gestao`) é que agora o menu e a rota
   * leem a MESMA regra — antes eram dois mecanismos independentes, e o do
   * backend não existia para metade das portas.
   */
  permissao?: string;
  /**
   * Seção do perfil — modelo antigo. Mantido só onde a tela ainda não tem porta
   * no backend (Estoque, Ocorrências, Pós-venda): permissão sem rota é mentira
   * na tela, então esses três continuam por seção até terem API.
   */
  secao?: string;
}

const OPERACAO: ItemMenu[] = [
  { to: '/inicio', icon: 'bi-house-door', nome: 'Início' },
  // A INBOX FICA AO LADO DO INÍCIO, e não num grupo próprio: as duas são telas
  // de COMPOSIÇÃO — não têm módulo, não têm tabela, e juntam o que vem de
  // vários lugares. O Início mostra o panorama; a Inbox, o que espera por você.
  //
  // Sem permissão, e é regra: é porta pessoal, todo mundo tem uma. Um
  // interruptor aqui seria o que ninguém jamais desmarcaria na tela de Acessos.
  //
  // ⚠️ `/autorizacoes` CONTINUA no Financeiro, de propósito: a aba da Inbox
  // mostra a mesma fila, mas tirar um item de menu que a Adriana e a Suany já
  // usam é decisão dele — e acrescentar não quebra ninguém, tirar sim.
  { to: '/inbox', icon: 'bi-inbox', nome: 'Inbox' },
  {
    to: '/ordens-servico',
    icon: 'bi-clipboard-check',
    nome: 'Ordens de Serviço',
    // `proprias.ver` e não `os.ver`: o vendedor e o instalador enxergam a tela,
    // com a lista filtrada nas próprias OS. Quem tem `os.ver` vê todas — a
    // diferença é o conteúdo, não o caminho.
    permissao: 'ordens-servico.proprias.ver',
  },
  {
    icon: 'bi-calendar-week',
    nome: 'Agenda',
    permissao: 'agenda.visitas.ver',
    sub: [
      // O grupo já diz "Agenda", então o sub-item só precisa dizer QUAL.
      //
      // Duas telas porque o trabalho é diferente: AGENDAMENTOS é onde se marca
      // (OS a agendar, arrastar, editar) e INSTALAÇÕES é a semana que o
      // instalador vê no celular. A empresa vai ter outras (férias, eventos da
      // loja) e elas entram aqui do lado.
      // Cada sub-item tem a SUA permissão, e não a do grupo. Enquanto as duas
      // dividiam `agenda.visitas.ver`, o instalador abria a agenda do mês
      // inteiro — que é o que ele não deve ver (27/08/2026).
      {
        to: '/agenda/agendamentos',
        icon: 'bi-calendar-plus',
        nome: 'Agendamentos',
        permissao: 'agenda.agendamentos.ver',
      },
      {
        to: '/agenda/instalacoes',
        icon: 'bi-calendar-week',
        nome: 'Instalações',
        permissao: 'agenda.instalacoes.ver',
      },
    ],
  },
  { to: '/viagens', icon: 'bi-suitcase-lg', nome: 'Viagens', permissao: 'viagens.viagem.ver' },
  // Estoque e Ocorrências continuam por `secao`: não têm uma rota sequer no
  // backend, e permissão que nenhuma rota exige é mentira na tela.
  { to: '/estoque', icon: 'bi-box-seam', nome: 'Estoque', secao: 'estoque' },
  {
    to: '/ocorrencias',
    icon: 'bi-exclamation-circle',
    nome: 'Ocorrências',
    secao: 'ocorrencias',
  },
];

const FINANCEIRO: ItemMenu[] = [
  // A LISTA mostra o que a equipe inteira gastou; lançar é outra coisa. "Por
  // enquanto, os funcionários vão apenas lançar despesas" (Vittorio, 09/08/2026)
  // — que era a marca `gestao: true` e agora é a permissão de ver todas.
  // PRIMEIRO item do grupo, e de propósito: lançar é o que a maioria vem fazer
  // aqui — instalador e vendedor em campo. Até 27/08/2026 não havia caminho
  // nenhum no menu para o formulário, e quem estava logado só conseguia lançar
  // pelo link público. `/despesas/nova` escolhe o arranjo pelo aparelho.
  {
    to: '/despesas/nova',
    icon: 'bi-plus-circle',
    nome: 'Lançar despesa',
    permissao: 'despesas.lancamento.lancar',
  },
  // POR LOJA (dele, 25/09): "fica mais difícil alguém lançar na loja errada".
  // Mesmo desenho de Custos de Pedido: uma página por loja, que já vem
  // filtrada e já lança naquela loja — e "Todas" para quem quer o conjunto.
  {
    icon: 'bi-cash-coin',
    nome: 'Despesas',
    permissao: 'despesas.lancamento.ver',
    sub: [
      { to: '/despesas/lista/bz', icon: 'bi-geo-alt', nome: 'Búzios' },
      { to: '/despesas/lista/tr', icon: 'bi-geo-alt', nome: 'Trancoso' },
      { to: '/despesas', icon: 'bi-collection', nome: 'Todas' },
    ],
  },
  // "Minhas despesas" vale para TODO MUNDO que lança, gestão inclusive — a
  // Adriana é admin e é dela a viagem cujas despesas ela quer conferir. Quem
  // lança tem direito de ver o que lançou.
  {
    to: '/despesas/minhas',
    icon: 'bi-receipt',
    nome: 'Minhas Despesas',
    permissao: 'despesas.proprias.ver',
  },
  {
    icon: 'bi-bar-chart-line',
    nome: 'Custos de Pedido',
    permissao: 'custos-pedido.pedido.ver',
    sub: [
      { to: '/custos-pedido/lista/bz', icon: 'bi-geo-alt', nome: 'Búzios' },
      { to: '/custos-pedido/lista/tr', icon: 'bi-geo-alt', nome: 'Trancoso' },
      { to: '/custos-pedido/ordens-compra', icon: 'bi-cart', nome: 'Ordens de compra' },
    ],
  },
  // `fila.ver`, e não `pedido.ver`: quem só tem `pedido.ver` continua vendo as
  // autorizações da PRÓPRIA OS, pela aba — o que sai é o caminho para a fila
  // geral, que é tela de gestão.
  {
    to: '/autorizacoes',
    icon: 'bi-patch-check',
    nome: 'Autorizações',
    permissao: 'autorizacoes.fila.ver',
  },
];

const COMERCIAL: ItemMenu[] = [
  // Sem rota no backend ainda — ver o comentário de Estoque.
  { to: '/pos-venda', icon: 'bi-headset', nome: 'Pós-venda', secao: 'pos-venda' },
];

// "Equipe" — as pessoas da empresa, e o que a gestão faz com elas.
//
// SEPARADO DE "Minha Área" em 28/08/2026. Era tudo "Pessoal", e a palavra
// carregava as duas leituras: *departamento pessoal* (onde Funcionários nasceu,
// decisão P1 de 09/08) e *do indivíduo*. Enquanto o grupo tinha três itens, a
// ambiguidade não incomodava; com ponto, férias e escala chegando, ela ia
// desandar — metade do grupo seria da gestão e metade seria de cada um.
//
// A regra que separa os dois é a MESMA que separa Preferências de
// Configurações: *"se eu mexer aqui, muda para os outros?"*. E ela divide até o
// que parecia indivisível — bater o próprio ponto é Minha Área; conferir o
// ponto da equipe é Equipe.
//
// A ROTA virou `/funcionarios` em 28/08: grupo de menu não entra na URL neste
// app (`/ordens-servico` não é `/operacao/ordens-servico`), e o `/pessoal/` era
// a única exceção — herdada de quando "Pessoal" queria dizer departamento. As
// quatro camadas voltaram a bater: `funcionarios.ficha.ver`, `/api/funcionarios`,
// `/funcionarios`, `pages/funcionarios/`. O endereço antigo segue como rampa.
const EQUIPE: ItemMenu[] = [
  // Até 18/08/2026 este item aparecia para TODO MUNDO e a rota respondia 403:
  // `GET /api/funcionarios` sempre foi `requer_admin`. Menu que promete o que a
  // rota recusa é o defeito que esta frente desfaz — ninguém perde acesso aqui,
  // perde-se o caminho para o erro.
  {
    to: '/funcionarios',
    icon: 'bi-people',
    nome: 'Funcionários',
    permissao: 'funcionarios.ficha.ver',
  },
];

// "Minha Área" — o que é de quem está logado, e de mais ninguém.
//
// Nenhum item leva permissão, e isso é regra, não esquecimento: o escopo já é o
// próprio dono. Permissão aqui seria um interruptor que ninguém jamais
// desmarcaria na tela de Acessos.
//
// "Meus Dados" é a ficha de quem tem ficha; "Acesso e Senha" é do login e serve
// a TODOS, inclusive sócio e contador, que não têm ficha (decisão P9). As duas
// ficam no menu, e não só no dropdown do avatar, porque ninguém acha item
// escondido atrás da própria foto — pedir "entra lá e preenche seus dados" só
// funciona se houver um lugar no menu para apontar.
//
// "Aparência" ENTRA NO MENU em 28/08/2026. Ela existia só no dropdown do avatar
// e no botão da paleta, e ele foi procurar onde trocava a cor sem achar. O nome
// era "Preferências" e prometia demais: avisos também são preferência, e são
// outra página.
const MINHA_AREA: ItemMenu[] = [
  { to: '/pessoal/meus-dados', icon: 'bi-card-text', nome: 'Meus Dados' },
  { to: '/pessoal/acesso', icon: 'bi-shield-lock', nome: 'Acesso e Senha' },
  { to: '/minha-area/aparencia', icon: 'bi-palette', nome: 'Aparência' },
  // "Notificações" é o AJUSTE do canal — em quais aparelhos eu quero ser
  // avisado. Não confundir com a central (a caixa de entrada), que é lugar de
  // visita diária e não mora aqui: "Minha Área é um setor que você não mexe
  // muito" (ele, 28/08/2026).
  { to: '/minha-area/notificacoes', icon: 'bi-bell', nome: 'Notificações' },
];

// "Sistema" — quem administra o app.
//
// Era `usuario === 'vmlb'`, escrito no código: regra de acesso invisível na
// tela, que nem a tela de Perfis & Acessos sabia explicar. Agora cada item diz
// a permissão que o abre, e todas elas são de admin.
const SISTEMA: ItemMenu[] = [
  { to: '/usuarios', icon: 'bi-people', nome: 'Usuários', permissao: 'usuarios.contas.ver' },
  {
    to: '/configuracoes/perfis',
    icon: 'bi-shield-check',
    nome: 'Perfis & Acessos',
    permissao: 'configuracoes.acessos.gerir',
  },
  {
    to: '/configuracoes',
    icon: 'bi-gear',
    nome: 'Configurações',
    // `parametros.ver` e não mais `tiny.ver`: a tela deixou de ser a do Tiny em
    // 28/08/2026 — ela abre nos PARÂMETROS e o Tiny virou uma aba. Item de menu
    // declara a permissão da primeira coisa que a tela mostra, senão promete o
    // que a pessoa não vai ver.
    //
    // Hoje ninguém perde nada com a troca: as duas são de administrador, e
    // admin recebe `TODAS` por código (`servico_permissoes.py:549`). O dia em
    // que um gestor precisar só das integrações, isto vira duas entradas.
    permissao: 'configuracoes.parametros.ver',
  },
];

/**
 * "Testes" — as bancadas de desenvolvimento.
 *
 * Não são funcionalidade do app: são telas que existem para OLHAR alguma coisa
 * enquanto uma decisão está aberta, e saem quando ela fecha. Ficam debaixo de
 * `configuracoes.acessos.gerir` porque quem administra o app é quem as usa —
 * a operação não deve nem ver o grupo, e o `secao()` some sozinho quando nenhum
 * item sobra.
 */
const TESTES: ItemMenu[] = [
  {
    to: '/dev/telas-celular',
    icon: 'bi-phone',
    nome: 'Telas no Celular',
    permissao: 'configuracoes.acessos.gerir',
  },
  {
    to: '/dev/campos-numericos',
    icon: 'bi-123',
    nome: 'Campos Numéricos',
    permissao: 'configuracoes.acessos.gerir',
  },
  {
    to: '/dev/moldura-pagina',
    icon: 'bi-window-sidebar',
    nome: 'Moldura da Página',
    permissao: 'configuracoes.acessos.gerir',
  },
];

/**
 * O NOME DE CADA TELA, POR CAMINHO — para a TRILHA saber dizer de onde a pessoa
 * veio mesmo em tela que ainda não usa a `CustomMolduraPagina`.
 *
 * POR QUE MORA AQUI. O menu já é a lista de telas do app com o nome que a
 * pessoa lê. Manter uma segunda lista em `lib/trilha.ts` criaria duas verdades,
 * e seria a desatualizada que alguém leria — o item novo entra no menu e
 * ninguém lembra do outro arquivo.
 *
 * ⚠️ A TRILHA PRECISA DISTO ENQUANTO A MIGRAÇÃO NÃO TERMINA. A moldura registra
 * na trilha quem a usa; sem esta lista, sair de uma tela ainda não migrada
 * (Viagens, por exemplo) e cair numa migrada mostrava o Voltar VAZIO — foi o
 * defeito que ele pegou em 31/08/2026. Quando as 52 telas estiverem migradas
 * isto continua útil, porque o menu é mais barato de manter do que 52 títulos.
 */
export function nomeDaTela(caminho: string): string | null {
  const todos = [
    ...OPERACAO, ...FINANCEIRO, ...COMERCIAL,
    ...EQUIPE, ...MINHA_AREA, ...SISTEMA, ...TESTES,
  ];
  let achado: { to: string; nome: string } | null = null;
  const considerar = (to?: string, nome?: string) => {
    if (!to || !nome) return;
    /* O MAIS ESPECÍFICO VENCE: `/despesas/lancar` casa com `/despesas` e com ele
       mesmo; sem esta comparação de tamanho, a ordem do menu decidiria. */
    if (caminho === to || caminho.startsWith(`${to}/`)) {
      if (!achado || to.length > achado.to.length) achado = { to, nome };
    }
  };
  for (const it of todos) {
    considerar(it.to, it.nome);
    for (const sub of it.sub ?? []) considerar(sub.to, sub.nome);
  }
  return achado ? (achado as { to: string; nome: string }).nome : null;
}

const CustomSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { usuario, logout, pode } = useAuth();

  const closeSidebar = () => {
    if (ehCelularAgora()) {
      document.body.classList.remove('sidebar-open');
      document.body.classList.add('sidebar-close');
    }
  };

  const sair = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const link = (it: ItemMenu) => {
    // Item com submenu (dropdown do tema AdminUX)
    if (it.sub) {
      return (
        <li className="nav-item dropdown" key={it.nome}>
          <a
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            role="button"
          >
            <i className={`menu-icon bi ${it.icon}`}></i>
            <div className="col menu-name">{it.nome}</div>
          </a>
          <ul className="dropdown-menu">
            {it.sub.filter(liberado).map((s) => (
              <li className="nav-item" key={s.to}>
                <NavLink className="nav-link" to={s.to} end onClick={closeSidebar}>
                  <i className={`menu-icon bi ${s.icon}`}></i>
                  <div className="col align-self-center menu-name">{s.nome}</div>
                </NavLink>
              </li>
            ))}
          </ul>
        </li>
      );
    }
    return (
      <li className="nav-item" key={it.to}>
        {/* `end`: o NavLink marca como ativo tudo o que COMEÇA com o caminho,
            então "/configuracoes" acendia junto com "/configuracoes/perfis" e
            os dois itens ficavam destacados ao mesmo tempo. Com `end`, só
            acende quem é exatamente a rota aberta.

            Vale para todos, e não só para o par que apareceu: qualquer item
            cujo caminho seja prefixo de outro teria o mesmo defeito, e ninguém
            lembraria de voltar aqui na hora de criar o próximo. */}
        <NavLink className="nav-link" to={it.to!} end onClick={closeSidebar}>
          <i className={`menu-icon bi ${it.icon}`}></i>
          <div className="col menu-name">{it.nome}</div>
        </NavLink>
      </li>
    );
  };

  const secoes = usuario?.secoes ?? [];
  // Um item aparece quando a pessoa alcança a permissão dele. O `secao` só
  // sobrevive nos três módulos que ainda não têm backend.
  const liberado = (it: ItemMenu): boolean => {
    if (it.permissao && !pode(it.permissao)) return false;
    if (it.secao && !secoes.includes(it.secao)) return false;
    // Grupo sem nenhum filho visível é um dropdown que abre vazio. Até
    // 27/08/2026 os sub-itens NÃO eram filtrados: `it.sub.map()` desenhava
    // todos, e o item de menu prometia uma tela que a rota agora recusa.
    if (it.sub) return it.sub.some(liberado);
    return true;
  };

  /** A seção some inteira quando nenhum item dela sobra — título órfão é ruído. */
  const secao = (titulo: string, itens: ItemMenu[]) => {
    const visiveis = itens.filter(liberado);
    if (visiveis.length === 0) return null;
    return (
      <>
        <div className="px-3 not-iconic mt-3 mb-1">
          <h6 className="menu-name mb-0">{titulo}</h6>
        </div>
        <ul className="nav flex-column menu-active-line">{visiveis.map(link)}</ul>
      </>
    );
  };

  /* ORDENS DE SERVIÇO: UM ITEM POR LOJA (29/10/2026). A lista de OS é uma
     página por loja, e o menu mostra só as lojas em que a pessoa enxerga OS
     (`lojas_os`, do âmbito). Com uma loja só, o item vai direto para ela — um
     dropdown de um filho é um clique a mais para nada. O caminho é o de
     `caminhoDaLoja` (pages/ordens-servico/shared.ts). */
  const lojasOS = usuario?.lojas_os ?? [];
  const paginaLoja = (codigo: string) => `/ordens-servico/loja/${codigo.toLowerCase()}`;
  /* A AGENDA segue o mesmo padrão, mas DENTRO do grupo (o dropdown do tema
     não tem segundo nível): um sub-item por tela × loja — "Agendamentos
     TR", com a sigla da loja (pedido dele, 29/10/2026). O menu é o ÚNICO lugar onde se escolhe a loja; a página não
     tem botão de loja nem "Todas" (decisão dele, 29/10/2026). Com uma loja
     só, o sub-item fica com o nome curto e leva direto a ela. */
  const porLoja = (s: SubItem, lojas_: LojaMenu[] | undefined): SubItem[] => {
    if (!lojas_ || lojas_.length === 0) return [s];
    const um = lojas_.length === 1;
    return lojas_.map((l) => ({
      ...s,
      to: `${s.to}/loja/${l.codigo.toLowerCase()}`,
      nome: um ? s.nome : `${s.nome} ${l.codigo}`,
    }));
  };
  const operacao = OPERACAO.map((it): ItemMenu => {
    if (it.nome === 'Agenda' && it.sub) {
      return {
        ...it,
        sub: it.sub.flatMap((s) =>
          s.to === '/agenda/agendamentos'
            ? porLoja(s, usuario?.lojas_agendamentos)
            : s.to === '/agenda/instalacoes'
              ? porLoja(s, usuario?.lojas_instalacoes)
              : [s],
        ),
      };
    }
    if (it.to !== '/ordens-servico' || lojasOS.length === 0) return it;
    if (lojasOS.length === 1) return { ...it, to: paginaLoja(lojasOS[0].codigo) };
    return {
      icon: it.icon,
      nome: it.nome,
      permissao: it.permissao,
      sub: lojasOS.map((l) => ({
        to: paginaLoja(l.codigo),
        icon: 'bi-shop',
        nome: l.nome_curto || l.nome,
        permissao: it.permissao,
      })),
    };
  });

  return (
    <div className="adminuiux-sidebar shadow-sm">
      <div className="adminuiux-sidebar-inner">
        {secao('Operação', operacao)}
        {secao('Financeiro', FINANCEIRO)}
        {secao('Comercial', COMERCIAL)}
        {secao('Equipe', EQUIPE)}
        {secao('Minha Área', MINHA_AREA)}
        {/* O grupo some sozinho quando nenhum item dele sobra — não precisa
            mais do `ehDono`, que era o hardcode do `vmlb`. */}
        {secao('Sistema', SISTEMA)}
        {secao('Testes', TESTES)}

        {/* Rodapé: usuário + sair */}
        <div className="mt-auto p-3 not-iconic">
          <div className="row gx-2 align-items-center">
            <div className="col-auto">
              {usuario?.foto ? (
                <figure className="avatar avatar-40 rounded-circle coverimg mb-0">
                  <img src={usuario.foto} alt="" />
                </figure>
              ) : (
                <span className="avatar avatar-40 rounded-circle bg-theme-1-subtle text-theme-1 d-flex align-items-center justify-content-center">
                  <i className="bi bi-person"></i>
                </span>
              )}
            </div>
            <div className="col">
              <p className="mb-0 fw-medium text-truncate">{usuario?.nome ?? '—'}</p>
              <p className="small text-secondary mb-0 text-truncate">
                {usuario?.perfil_nome ?? usuario?.perfil ?? ''}
              </p>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-link btn-square text-danger"
                type="button"
                onClick={sair}
                title="Sair"
              >
                <i className="bi bi-box-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSidebar;
